import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ComplianceMonitoringProgramme, ComplianceMonitoringPlanItem } from './types';
import { monitoringService } from './monitoringService';

interface ComplianceProgrammeModalProps {
  programmes: ComplianceMonitoringProgramme[];
  planItems: ComplianceMonitoringPlanItem[];
  organizationId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const ComplianceProgrammeModal: React.FC<ComplianceProgrammeModalProps> = ({
  programmes,
  planItems,
  organizationId,
  onClose,
  onRefresh,
}) => {
  const [selectedProgrammeId, setSelectedProgrammeId] = useState<string>(
    programmes[0]?.id || ''
  );
  const [isCreatingProgramme, setIsCreatingProgramme] = useState(false);
  const [newProgramme, setNewProgramme] = useState({
    programme_number: `CMP-PROG-${new Date().getFullYear() + 1}`,
    title: `${new Date().getFullYear() + 1} Annual Monitoring Programme`,
    programme_year: new Date().getFullYear() + 1,
    period_start: `${new Date().getFullYear() + 1}-01-01`,
    period_end: `${new Date().getFullYear() + 1}-12-31`,
    is_risk_based: true,
    lead_department: 'Safety & Compliance Directorate',
    programme_manager_name: 'Lead Compliance Officer',
  });

  const [isCreatingPlanItem, setIsCreatingPlanItem] = useState(false);
  const [newPlanItem, setNewPlanItem] = useState({
    title: '',
    item_code: `CMP-PLAN-${String(planItems.length + 1).padStart(3, '0')}`,
    review_method: 'ONSITE_AUDIT',
    priority: 'HIGH',
    planned_quarter: 'Q1',
    planned_start_date: `${new Date().getFullYear()}-04-01`,
    planned_end_date: `${new Date().getFullYear()}-04-05`,
  });

  const activeProgramme = programmes.find((p) => p.id === selectedProgrammeId);
  const filteredPlanItems = planItems.filter((i) => i.programme_id === selectedProgrammeId);

  const handleCreateProgramme = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await monitoringService.createProgramme({
        ...newProgramme,
        organization_id: organizationId,
        status: 'DRAFT',
      });
      setIsCreatingProgramme(false);
      onRefresh();
    } catch (err) {
      alert('Failed to create programme: ' + (err as Error).message);
    }
  };

  const handleCreatePlanItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProgrammeId) return;
    try {
      await monitoringService.createPlanItem({
        ...newPlanItem,
        programme_id: selectedProgrammeId,
        organization_id: organizationId,
        status: 'SCHEDULED',
      });
      setIsCreatingPlanItem(false);
      onRefresh();
    } catch (err) {
      alert('Failed to schedule plan item: ' + (err as Error).message);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Compliance Monitoring Programmes & Plan Register</h3>
            <p>Annual assurance programmes, scheduled quarterly plan items, and risk coverage</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          {/* Top Programme Selector / Creator */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: 'var(--bg-subtle, #f8fafc)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary, #334155)' }}>Select Programme:</label>
              <select
                value={selectedProgrammeId}
                onChange={(e) => setSelectedProgrammeId(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color, #cbd5e1)', background: 'var(--card-bg, #fff)', fontSize: '13px' }}
              >
                {programmes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.programme_number} - {p.title} ({p.programme_year}) [{p.status}]
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setIsCreatingProgramme(true)}
              className="btn-primary"
            >
              + New Programme
            </button>
          </div>

          {/* Programme details card */}
          {activeProgramme && (
            <div style={{ background: 'var(--card-bg, #fff)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Programme Year</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{activeProgramme.programme_year}</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead Department</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{activeProgramme.lead_department}</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manager</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{activeProgramme.programme_manager_name || 'Unassigned'}</span>
              </div>
              <div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status / Methodology</span>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#2563eb' }}>
                  {activeProgramme.status} {activeProgramme.is_risk_based && '• Risk-Based'}
                </span>
              </div>
            </div>
          )}

          {/* Plan Items Sub-section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)', margin: 0 }}>
                Scheduled Plan Items ({filteredPlanItems.length})
              </h4>
              <button
                type="button"
                onClick={() => setIsCreatingPlanItem(true)}
                className="btn-secondary"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                + Schedule Plan Item
              </button>
            </div>

            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid var(--border-color, #e2e8f0)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead style={{ background: 'var(--bg-subtle, #f8fafc)', borderBottom: '1px solid var(--border-color, #e2e8f0)', fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted, #64748b)' }}>
                  <tr>
                    <th style={{ padding: '10px 14px' }}>Item Code</th>
                    <th style={{ padding: '10px 14px' }}>Title</th>
                    <th style={{ padding: '10px 14px' }}>Quarter</th>
                    <th style={{ padding: '10px 14px' }}>Method</th>
                    <th style={{ padding: '10px 14px' }}>Priority</th>
                    <th style={{ padding: '10px 14px' }}>Status</th>
                    <th style={{ padding: '10px 14px' }}>Target Window</th>
                  </tr>
                </thead>
                <tbody style={{ background: 'var(--card-bg, #fff)' }}>
                  {filteredPlanItems.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted, #64748b)' }}>
                        No plan items registered under this programme.
                      </td>
                    </tr>
                  ) : (
                    filteredPlanItems.map((item) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#2563eb', fontWeight: 600 }}>{item.item_code}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary, #0f172a)', fontWeight: 500 }}>{item.title}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', background: 'var(--bg-subtle, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 600 }}>
                            {item.planned_quarter || 'TBD'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-muted, #64748b)', fontSize: '12px' }}>{item.review_method}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: item.priority === 'CRITICAL' ? '#fee2e2' : item.priority === 'HIGH' ? '#fef3c7' : '#f1f5f9',
                              color: item.priority === 'CRITICAL' ? '#991b1b' : item.priority === 'HIGH' ? '#92400e' : '#475569',
                            }}
                          >
                            {item.priority}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, background: '#e0f2fe', color: '#0369a1' }}>
                            {item.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-muted, #64748b)', fontSize: '12px' }}>
                          {item.planned_start_date ? `${item.planned_start_date} ~ ${item.planned_end_date || ''}` : 'Pending'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>

      {/* Child Modal: New Programme */}
      {isCreatingProgramme && (
        <div className="modal-backdrop" style={{ zIndex: 60 }}>
          <div className="modal-card max-w-md">
            <div className="modal-header">
              <div>
                <h3>Create Annual Monitoring Programme</h3>
                <p>Define an annual framework for compliance monitoring</p>
              </div>
              <button type="button" onClick={() => setIsCreatingProgramme(false)} className="close-btn" aria-label="Close modal">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateProgramme}>
              <div className="modal-body">
                <div className="form-field">
                  <label>Programme Number</label>
                  <input
                    type="text"
                    value={newProgramme.programme_number}
                    onChange={(e) => setNewProgramme({ ...newProgramme, programme_number: e.target.value })}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Title</label>
                  <input
                    type="text"
                    value={newProgramme.title}
                    onChange={(e) => setNewProgramme({ ...newProgramme, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Year</label>
                    <input
                      type="number"
                      value={newProgramme.programme_year}
                      onChange={(e) => setNewProgramme({ ...newProgramme, programme_year: parseInt(e.target.value) || 2026 })}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Lead Dept</label>
                    <input
                      type="text"
                      value={newProgramme.lead_department}
                      onChange={(e) => setNewProgramme({ ...newProgramme, lead_department: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setIsCreatingProgramme(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Programme
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Child Modal: New Plan Item */}
      {isCreatingPlanItem && (
        <div className="modal-backdrop" style={{ zIndex: 60 }}>
          <div className="modal-card max-w-md">
            <div className="modal-header">
              <div>
                <h3>Schedule Monitoring Plan Item</h3>
                <p>Add scheduled audit or review task to current programme</p>
              </div>
              <button type="button" onClick={() => setIsCreatingPlanItem(false)} className="close-btn" aria-label="Close modal">
                ✕
              </button>
            </div>
            <form onSubmit={handleCreatePlanItem}>
              <div className="modal-body">
                <div className="form-field">
                  <label>Item Code</label>
                  <input
                    type="text"
                    value={newPlanItem.item_code}
                    onChange={(e) => setNewPlanItem({ ...newPlanItem, item_code: e.target.value })}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Title / Target Focus</label>
                  <input
                    type="text"
                    value={newPlanItem.title}
                    onChange={(e) => setNewPlanItem({ ...newPlanItem, title: e.target.value })}
                    required
                    placeholder="e.g. Flight Operations ETOPS Audit"
                  />
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Quarter</label>
                    <select
                      value={newPlanItem.planned_quarter}
                      onChange={(e) => setNewPlanItem({ ...newPlanItem, planned_quarter: e.target.value })}
                    >
                      <option value="Q1">Q1</option>
                      <option value="Q2">Q2</option>
                      <option value="Q3">Q3</option>
                      <option value="Q4">Q4</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Priority</label>
                    <select
                      value={newPlanItem.priority}
                      onChange={(e) => setNewPlanItem({ ...newPlanItem, priority: e.target.value })}
                    >
                      <option value="LOW">LOW</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="HIGH">HIGH</option>
                      <option value="CRITICAL">CRITICAL</option>
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>Method</label>
                  <select
                    value={newPlanItem.review_method}
                    onChange={(e) => setNewPlanItem({ ...newPlanItem, review_method: e.target.value })}
                  >
                    <option value="ONSITE_AUDIT">Onsite Audit</option>
                    <option value="DESK_ASSESSMENT">Desk Assessment</option>
                    <option value="INSPECTION">Inspection</option>
                    <option value="SAMPLING">Sampling</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setIsCreatingPlanItem(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Schedule Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
